import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import clsx from 'clsx'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'

import { TApi, TConnectionStatus } from '../../../../shared/const'
import { stateLabel } from '../../../../shared/helpers'
import http from '../../../../shared/http'
import { optionLabel } from '../../../../shared/optionList'
import { formatVienna } from '../../../../shared/datetime'

import Btn from '../../../../UI/Btn'
import MiniHeader from '../../../../UI/MiniHeader'
import Loading from '../../../../UI/Loading'
import ReminderNote from '../../../../UI/ReminderNote'

import styles from './style.module.scss'

/**
 * Die bevorzugten Kontaktwege als Satzteil („E-Mail oder Telefon").
 * Dieselben Bezeichnungen wie im Profil (contactMode…).
 */
export const contactModesPhrase = (modes, t) => {
	const list = (modes || []).map(mode => t(`contactMode${mode}`))
	if (!list.length) return null
	if (list.length === 1) return list[0]
	return t('listOr', { first: list.slice(0, -1).join(', '), last: list[list.length - 1] })
}

/**
 * Anfragen und Verbindungen einer Unterstuetzer:in zu Junior Companies.
 * Offene Anfragen koennen hier angenommen oder abgelehnt werden.
 *
 * Seit Anforderung #232 steht an jeder Anfrage, was die Junior Company macht
 * (Geschaeftsidee), wobei sie Unterstuetzung sucht und welche Kompetenzen sie
 * anspricht - und an offenen Anfragen, was nach einer Zusage passiert. Das
 * gehoert *vor* die Entscheidung, nicht in die Bestaetigung danach.
 */
export default function SupporterConnections() {
	const { t, i18n } = useTranslation()

	// Branchen sind englisch gespeichert, angezeigt gehört die Fassung der Sprache.
	const branches = useSelector(state => state.options?.branch) || []

	const [connections, setConnections] = useState(null)
	const [pendingId, setPendingId] = useState(null)

	const load = useCallback(() => {
		http.get(TApi.SUPPORTER_CONNECTIONS)
			.then(({ data }) => setConnections(data))
			.catch(() => {
				// Meldung kommt aus shared/http
				setConnections([])
			})
		// t bewusst NICHT in den Abhaengigkeiten: useTranslation liefert bei
		// jedem Render eine neue Funktionsreferenz. Mit t in der Liste entstuende
		// eine Endlosschleife Render -> Fetch -> Render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		load()
	}, [load])

	/*
	 * Beenden fragt nach: Es betrifft die andere Seite und loest eine Nachricht
	 * aus. Annehmen und Ablehnen tun das nicht - dort ist die Anfrage ohnehin
	 * eine offene Frage, die beantwortet werden will.
	 */
	const endCollaboration = connection => {
		Swal.fire({
			title: t('endCollaborationTitle'),
			text: t('endCollaborationText', { company: connection.company?.name || '' }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('endCollaboration'),
			cancelButtonText: t('Cancel'),
			confirmButtonColor: '#d33'
		}).then(result => {
			if (result.isConfirmed) respond(connection.id, TConnectionStatus.ENDED)
		})
	}

	const respond = (id, status) => {
		setPendingId(id)
		http.put(TApi.SUPPORTER_CONNECTION_ID(id), { status })
			.then(() => {
				toast.success(t('Saved successfully'))
				load()
			})
			// Die Meldung kommt aus shared/http — hier bleibt nur aufzuräumen.
			.catch(() => {})
			.finally(() => setPendingId(null))
	}

	if (!connections) return <Loading />

	return (
		<div className={styles.root}>
			<MiniHeader className={styles.header} title={t('myConnections')} bg="#f8faff" />

			{!connections.length ? (
				<div className={styles.empty}>{t('noConnectionsYet')}</div>
			) : (
				<div className={styles.list}>
					{connections.map(connection => (
						<article key={connection.id} className={styles.card}>
							<div className={styles.info}>
								<span className={styles.company}>{connection.company?.name || '-'}</span>
								<span className={styles.meta}>
									{stateLabel(connection.company?.state)}
									{connection.company?.branch
										? ` · ${optionLabel(branches, connection.company.branch, i18n.language)}`
										: ''}
								</span>
								{connection.company?.businessIdea && (
									<div className={styles.block}>
										<span className={styles.blockLabel}>{t('connectionBusinessIdea')}</span>
										<p className={styles.message}>{connection.company.businessIdea}</p>
									</div>
								)}
								{connection.requestMessage && (
									<div className={styles.block}>
										<span className={styles.blockLabel}>{t('connectionRequestNeed')}</span>
										<p className={styles.message}>{connection.requestMessage}</p>
									</div>
								)}
								{Boolean(connection.requestCompetences?.length) && (
									<div className={styles.block}>
										<span className={styles.blockLabel}>{t('connectionRequestCompetences')}</span>
										<div className={styles.tags}>
											{connection.requestCompetences.map(item => (
												<span key={item} className={styles.tag}>
													{item}
												</span>
											))}
										</div>
									</div>
								)}
								{connection.status === TConnectionStatus.REQUESTED && connection.contactDays && (
									<div className={styles.nextStep}>
										<strong>{t('connectionNextStepTitle')}</strong>
										<p>
											{contactModesPhrase(connection.contactModes, t)
												? t('connectionNextStepText', {
														days: connection.contactDays,
														modes: contactModesPhrase(connection.contactModes, t)
												  })
												: t('connectionNextStepTextNoModes', { days: connection.contactDays })}
										</p>
									</div>
								)}
								{connection.status === TConnectionStatus.CONNECTED && connection.contactConfirmedAt && (
									<p className={styles.confirmed}>
										{t('connectionContactConfirmed', {
											date: formatVienna(connection.contactConfirmedAt, 'dd.MM.yyyy')
										})}
									</p>
								)}
								{/*
								 * Auch hier sichtbar, damit eine Erinnerung im Postfach
								 * nachvollziehbar ist und nicht wie eine zweite Anfrage
								 * der Junior Company wirkt.
								 */}
								<ReminderNote
									audience="recipient"
									status={connection.status}
									reminderCount={connection.reminderCount}
									lastReminderAt={connection.lastReminderAt}
									autoDeclined={connection.autoDeclined}
								/>
							</div>

							<div className={styles.side}>
								<span className={clsx(styles.status, styles[`status${connection.status}`])}>
									{t(`connectionStatus${connection.status}`)}
								</span>

								{connection.status === TConnectionStatus.REQUESTED && (
									<div className={styles.actions}>
										<Btn
											size="small"
											theme="success"
											disabled={pendingId === connection.id}
											onClick={() => respond(connection.id, TConnectionStatus.CONNECTED)}
										>
											{t('acceptRequest')}
										</Btn>
										<Btn
											size="small"
											theme="danger"
											disabled={pendingId === connection.id}
											onClick={() => respond(connection.id, TConnectionStatus.DECLINED)}
										>
											{t('declineRequest')}
										</Btn>
									</div>
								)}

								{/*
								 * Eine laufende Zusammenarbeit darf jede Seite beenden
								 * (Anforderung #27). Mit Rückfrage, weil es die andere Seite
								 * betrifft und eine Nachricht auslöst.
								 */}
								{connection.status === TConnectionStatus.CONNECTED && (
									<div className={styles.actions}>
										<Btn
											size="small"
											theme="danger"
											disabled={pendingId === connection.id}
											onClick={() => endCollaboration(connection)}
										>
											{t('endCollaboration')}
										</Btn>
									</div>
								)}
							</div>
						</article>
					))}
				</div>
			)}
		</div>
	)
}
